// vg-binding: P7.D-15
// vg-binding: P7.D-16
// vg-binding: P7.D-164
// vg-binding: P7.D-165
//
// apps/vendor/src/features/products/variants/MatrixCellEditor.tsx
//
// Inline editor for a single variant cell — enable toggle + markup +
// per-SKU dimensions (Bug 7) + per-SKU ops fields processing/capacity/weight
// + external_sku (Amendment #4). Image-group editing happens at the column
// (color) level via ColorImageGroup, not per cell.

import { cn } from '@printway/ui';

import { useTranslation } from '../../../lib/i18n/index.js';

import type { VariantDraft } from './types.js';

interface Props {
  cell: VariantDraft;
  onChange: (next: VariantDraft) => void;
  /** R7-01 — fields the server refused as out of range (`dimensions.length`…). */
  errors?: string[] | undefined;
  /**
   * D17 / D16a — the product is priced per SKU: the cell edits its own cost
   * (`cost_cents`) instead of a markup over the product base.
   */
  perSku?: boolean;
}

/** Fields with their own range message (`variants.cellEditor.outOfRange.*`). */
const RANGE_MESSAGE_FIELDS = ['processing_time_days', 'capacity_per_day', 'weight_grams', 'weight_grams_override', 'dimensions'] as const;

export function MatrixCellEditor({ cell, onChange, errors, perSku = false }: Props): JSX.Element {
  const { t } = useTranslation('products');
  const bad = new Set(errors ?? []);
  const invalid = (field: string): string => (bad.has(field) ? 'border-red-500' : 'border-pw-border');
  const messages = RANGE_MESSAGE_FIELDS.filter((f) =>
    f === 'dimensions' ? [...bad].some((b) => b.startsWith('dimensions.')) : bad.has(f),
  );
  return (
    <div
      data-testid={`variant-cell-${cell.size}-${cell.color}`}
      className={cn(
        'flex flex-col gap-1 rounded-md border p-2 text-xs transition-colors',
        cell.enabled
          ? 'border-pw-border bg-pw-bg'
          : 'border-pw-border bg-pw-surface opacity-60',
      )}
    >
      <label className="flex items-center gap-1">
        <input
          type="checkbox"
          checked={cell.enabled}
          onChange={(e) => onChange({ ...cell, enabled: e.target.checked })}
          data-testid={`variant-enable-${cell.size}-${cell.color}`}
        />
        <span className="font-mono">{cell.size}</span>
      </label>
      {perSku ? (
        <label className="flex items-center gap-1">
          <span className="text-pw-text-muted">$</span>
          <input
            type="number"
            step="0.01"
            min={0}
            aria-label={t('variants.cellEditor.ariaCost')}
            data-testid={`variant-cost-${cell.size}-${cell.color}`}
            value={typeof cell.cost_cents === 'number' ? (cell.cost_cents / 100).toFixed(2) : ''}
            onChange={(e) => {
              const v = Number.parseFloat(e.target.value);
              onChange({ ...cell, cost_cents: Number.isFinite(v) ? Math.max(0, Math.round(v * 100)) : null });
            }}
            disabled={!cell.enabled}
            className={`h-7 w-16 rounded border ${invalid('cost_cents')} bg-pw-bg px-1 text-xs focus:border-pw-primary focus:outline-none`}
          />
        </label>
      ) : (
        <label className="flex items-center gap-1">
          <span className="text-pw-text-muted">+$</span>
          <input
            type="number"
            step="0.01"
            min={0}
            aria-label={t('variants.cellEditor.ariaMarkup')}
            data-testid={`variant-markup-${cell.size}-${cell.color}`}
            value={(cell.cost_markup_cents / 100).toFixed(2)}
            onChange={(e) => {
              const v = Number.parseFloat(e.target.value);
              const cents = Number.isFinite(v) ? Math.round(v * 100) : 0;
              onChange({ ...cell, cost_markup_cents: Math.max(0, cents) });
            }}
            disabled={!cell.enabled}
            className="h-7 w-16 rounded border border-pw-border bg-pw-bg px-1 text-xs focus:border-pw-primary focus:outline-none"
          />
        </label>
      )}
      {/* Bug 7 — per-variant packaging dimensions (cm): L × W × H. */}
      <div className="flex items-center gap-0.5">
        {(['length', 'width', 'height'] as const).map((axis, i) => (
          <span key={axis} className="flex items-center gap-0.5">
            {i > 0 ? <span className="text-pw-text-muted">×</span> : null}
            <input
              type="number"
              step="0.1"
              min={0}
              aria-label={`${axis} (cm)`}
              data-testid={`variant-dim-${axis}-${cell.size}-${cell.color}`}
              value={cell.dimensions[axis]}
              onChange={(e) => {
                const v = Number.parseFloat(e.target.value);
                onChange({
                  ...cell,
                  dimensions: {
                    ...cell.dimensions,
                    [axis]: Number.isFinite(v) ? Math.max(0, v) : 0,
                  },
                });
              }}
              disabled={!cell.enabled}
              className={`h-7 w-12 rounded border ${invalid(`dimensions.${axis}`)} bg-pw-bg px-1 text-xs focus:border-pw-primary focus:outline-none`}
            />
          </span>
        ))}
        <span className="text-[10px] text-pw-text-muted">cm</span>
      </div>
      {/* Amendment #4 — per-SKU ops fields. */}
      <div className="flex items-center gap-0.5">
        <input
          type="number"
          step="1"
          min={0}
          aria-label={t('variants.cellEditor.ariaProcessing')}
          data-testid={`variant-processing-${cell.size}-${cell.color}`}
          value={cell.processing_time_days}
          onChange={(e) => {
            const v = Number.parseInt(e.target.value, 10);
            onChange({
              ...cell,
              processing_time_days: Number.isFinite(v) ? Math.max(0, v) : 0,
            });
          }}
          disabled={!cell.enabled}
          placeholder={t('variants.cellEditor.placeholderDays')}
          className={`h-7 w-12 rounded border ${invalid('processing_time_days')} bg-pw-bg px-1 text-xs focus:border-pw-primary focus:outline-none`}
        />
        <input
          type="number"
          step="1"
          min={0}
          aria-label={t('variants.cellEditor.ariaCapacity')}
          data-testid={`variant-capacity-${cell.size}-${cell.color}`}
          value={cell.capacity_per_day}
          onChange={(e) => {
            const v = Number.parseInt(e.target.value, 10);
            onChange({
              ...cell,
              capacity_per_day: Number.isFinite(v) ? Math.max(0, v) : 0,
            });
          }}
          disabled={!cell.enabled}
          placeholder={t('variants.cellEditor.placeholderCapPerDay')}
          className={`h-7 w-14 rounded border ${invalid('capacity_per_day')} bg-pw-bg px-1 text-xs focus:border-pw-primary focus:outline-none`}
        />
        <input
          type="number"
          step="1"
          min={0}
          aria-label={t('variants.cellEditor.ariaWeight')}
          data-testid={`variant-weight-${cell.size}-${cell.color}`}
          value={cell.weight_grams}
          onChange={(e) => {
            const v = Number.parseFloat(e.target.value);
            onChange({
              ...cell,
              weight_grams: Number.isFinite(v) ? Math.max(0, v) : 0,
            });
          }}
          disabled={!cell.enabled}
          placeholder={t('variants.cellEditor.placeholderGrams')}
          className={`h-7 w-12 rounded border ${invalid('weight_grams')} bg-pw-bg px-1 text-xs focus:border-pw-primary focus:outline-none`}
        />
      </div>
      {messages.length > 0 ? (
        <ul data-testid={`variant-cell-errors-${cell.size}-${cell.color}`} className="text-[10px] text-red-600">
          {messages.map((f) => (
            <li key={f}>{t(`variants.cellEditor.outOfRange.${f}`)}</li>
          ))}
        </ul>
      ) : null}
      {/* Amendment #4 — external SKU mapping. */}
      <input
        type="text"
        maxLength={64}
        aria-label={t('variants.cellEditor.ariaExternalSku')}
        data-testid={`variant-external-sku-${cell.size}-${cell.color}`}
        value={cell.external_sku ?? ''}
        onChange={(e) => onChange({ ...cell, external_sku: e.target.value })}
        disabled={!cell.enabled}
        placeholder={t('variants.cellEditor.placeholderExternalSku')}
        className="h-7 w-full rounded border border-pw-border bg-pw-bg px-1 text-xs focus:border-pw-primary focus:outline-none"
      />
    </div>
  );
}

export default MatrixCellEditor;
